/**
 * Loudness at build (ADR-085 decision 5): one linear gain per master, to its folder's integrated target where the
 * master's integrated loudness is finite, otherwise its true peak to the folder's peak target, and never past the
 * true-peak ceiling.
 */

/**
 * A folder's targets: integrated loudness in LUFS, and the true peak in dBTP for a sound too short to measure.
 */
export interface LoudnessTarget {
  readonly integrated: number;
  readonly shortPeak: number;
}

/**
 * What the build measures of a master: integrated loudness in LUFS, `-Infinity` when it is too short for the 400 ms
 * gating blocks, and true peak in dBTP.
 */
export interface LoudnessMeasure {
  readonly integrated: number;
  readonly truePeak: number;
}

/**
 * The highest true peak a built sound may have, in dBTP.
 */
export const TRUE_PEAK_CEILING = -1;

/**
 * Each built folder's targets; the most specific folder of a hook decides.
 */
export const LOUDNESS_TARGETS: Readonly<Record<string, LoudnessTarget>> = Object.freeze({
  stingers: Object.freeze({ integrated: -16, shortPeak: -3 }),
  sfx: Object.freeze({ integrated: -18, shortPeak: -3 }),
  "sfx/ui": Object.freeze({ integrated: -22, shortPeak: -9 }),
  ambience: Object.freeze({ integrated: -26, shortPeak: -12 }),
  music: Object.freeze({ integrated: -16, shortPeak: -3 }),
});

/**
 * The targets of a hook: those of the longest folder of `LOUDNESS_TARGETS` it lies in.
 *
 * @param hook The hook.
 * @returns The targets, or null when the hook lies in no built folder.
 */
export function loudnessTargetOf(hook: string): LoudnessTarget | null {
  const segments = hook.split("/");
  for (let length = segments.length - 1; length > 0; length -= 1) {
    const target = LOUDNESS_TARGETS[segments.slice(0, length).join("/")];
    if (target !== undefined) {
      return target;
    }
  }
  return null;
}

/**
 * The gain, in dB, that brings a master to its target without passing the ceiling.
 *
 * @param measure The master's measured loudness and true peak.
 * @param target Its folder's targets.
 * @param ceiling The highest true peak allowed, in dBTP.
 * @returns The gain in dB; 0 for silence.
 */
export function normalisationGain(measure: LoudnessMeasure, target: LoudnessTarget, ceiling: number = TRUE_PEAK_CEILING): number {
  if (!Number.isFinite(measure.truePeak)) {
    return 0;
  }
  const wanted = Number.isFinite(measure.integrated) ? target.integrated - measure.integrated : target.shortPeak - measure.truePeak;
  return Math.min(wanted, ceiling - measure.truePeak);
}
